import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate, createSearchParams } from 'react-router-dom';
import { Breadcrumb, Title } from '../../Components/Atoms';
import { DatePicker } from '../../Components/Molecules';
import { Button, Spinner } from '@chakra-ui/react';
import { AuthContext, StoreContext } from '../../Library/contexts';
import { getRouteTrips, getRouteStats, getRoutes } from '../../APIs';
import { DATE_RANGES, DATE_OPTIONS } from '../../Library/constants';
import { stampToDate, getISODate, getLocalisedDate, secondsToHms, getLocalisedTime } from '../../Library/datetime';
import { ArrowRightIcon } from '../../Assets/images/svgs';
import "../PastRides/styles.css";
import PATHS from '../../Routes/paths';
import "./styles.css";

const RouteHistory = () => {
    const { filters, allVehicleRoutes, setAllVehicleRoutes, userVehicles, activeDriver } = useContext(StoreContext);
    const { profile } = useContext(AuthContext);

    // For driverLogin accounts, scope all history to the active driver's idx
    const driverIdForHistory = activeDriver ? activeDriver.idx : profile?.idx;
    const [loading, setLoading] = useState(false);
    
    // Data States
    const [groupedHistory, setGroupedHistory] = useState({});
    const [dailyRoutes, setDailyRoutes] = useState([]);
    const [routeTrips, setRouteTrips] = useState([]); // Level 3 Data
    
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const selectedDateParam = searchParams.get("date");
    const selectedRouteParam = searchParams.get("routeId");

    // Dynamic Breadcrumbs
    const getBreadcrumbs = () => {
        const crumbs = [
            { idx: 1, name: "Route History", link: selectedDateParam ? PATHS.ROUTE_HISTORY : null, isCurrentPage: !selectedDateParam },
        ];

        if (selectedDateParam) {
            crumbs.push({ 
                idx: 2, 
                name: selectedDateParam, 
                link: selectedRouteParam ? `${PATHS.ROUTE_HISTORY}?date=${selectedDateParam}` : null,
                isCurrentPage: !selectedRouteParam 
            });
        }

        if (selectedRouteParam) {
            const routeName = allVehicleRoutes?.find(r => String(r.idx) === String(selectedRouteParam))?.nm || `Route ${selectedRouteParam}`;
            crumbs.push({ idx: 3, name: routeName, isCurrentPage: true });
        }
        
        return crumbs;
    };

    useEffect(() => {
        if (selectedDateParam && selectedRouteParam) {
            fetchTripsForRoute(selectedDateParam, selectedRouteParam);
        } else if (selectedDateParam) {
            fetchDailyRouteStats(selectedDateParam);
        } else {
            fetchHistory();
        }
    }, [filters.range, selectedDateParam, selectedRouteParam, driverIdForHistory, allVehicleRoutes]);

 

    // Ensure Routes are loaded for Name lookup
    useEffect(() => {
        if (!allVehicleRoutes || !allVehicleRoutes.length) {
             getRoutes((err, data) => {
               if (err) console.error(err);
               else {
                 let activeRoutes = data.filter(route => !route.archived);
                 setAllVehicleRoutes(activeRoutes);
               }
             });
        }
    }, []); 

    // Level 1: Fetch History (Grouped by Date)
    const fetchHistory = () => {
        if (!driverIdForHistory) return;
        setLoading(true);
        const selectedDate = DATE_RANGES[filters.range];
        
        getRouteTrips({
            driverId: driverIdForHistory,
            from: selectedDate.from,
            to: selectedDate.to
        }, (err, requests) => {
            if (err) { console.error(err); setLoading(false); return; }
            if (!requests || !requests.length) { setGroupedHistory({}); setLoading(false); return; }

            const vids = [...new Set(requests.map(r => r.vid))];
            
            getRouteStats({ fromTs: selectedDate.from, toTs: selectedDate.to, vids: vids }, (statErr, routeStats) => {
                 setLoading(false);
                 const historyMap = {};
                 requests.forEach(req => {
                     const dateStr = stampToDate(req.ts || req.startTs, "D MMM YYYY");
                     if (!historyMap[dateStr]) {
                         historyMap[dateStr] = { date: dateStr, routes: new Set(), totalExpectedDist: 0, totalActualDist: 0 };
                     }
                     const dayEntry = historyMap[dateStr];
                     if (req.routeId) dayEntry.routes.add(req.routeId);
                     
                     // Simply aggregating for high level view
                     // Note: To be perfectly accurate we repeats stats lookup logic, 
                     // but for speed here we assume we can sum matching stats.
                     // Reusing the logic from Level 2 is better for consistency but verbose here.
                     // Simplified aggregation:
                     let actualDist = 0;
                     if(routeStats) {
                        routeStats.forEach(rs => {
                            if(rs.routes) rs.routes.forEach(r => {
                                if(String(r.routeId) === String(req.routeId) && r.tripStats) {
                                    r.tripStats.forEach(ts => {
                                        if (ts.requestIdx && String(ts.requestIdx) === String(req.idx)) actualDist = ts.dst || 0;
                                        else if (!ts.requestIdx && String(ts.driverId) === String(driverIdForHistory)) {
                                            if (Math.abs((ts.startTs || 0) - (req.ts || 0)) < 300000) actualDist = ts.dst || 0;
                                        }
                                    });
                                    if (!dayEntry.routes[req.routeId]?.expectedDist) req.expectedDist = r.routeDst;
                                }
                            });
                        });
                     }

                     if (!req.expectedDist) {
                        const fallbackDist = allVehicleRoutes?.find(rv => String(rv.idx) === String(req.routeId))?.totalDistance || 0;
                        req.expectedDist = fallbackDist * 1000;
                     }

                     dayEntry.totalExpectedDist += (req.expectedDist || 0);
                     dayEntry.totalActualDist += actualDist;
                 });
                 Object.values(historyMap).forEach(entry => entry.routeCount = entry.routes.size);
                 setGroupedHistory(historyMap);
             });
        });
    }

    // Level 2: Fetch Routes for Date
    const fetchDailyRouteStats = (dateStr) => {
        if (!driverIdForHistory) return;
        setLoading(true);
        const dayStart = new Date(dateStr).setHours(0,0,0,0);
        const dayEnd = new Date(dateStr).setHours(23,59,59,999);

        getRouteTrips({ driverId: driverIdForHistory, from: dayStart, to: dayEnd }, (err, requests) => {
             if (err || !requests || !requests.length) { setDailyRoutes([]); setLoading(false); return; }
             const vids = [...new Set(requests.map(r => r.vid))];
             
             getRouteStats({ fromTs: dayStart, toTs: dayEnd, vids: vids }, (statErr, routeStats) => {
                 setLoading(false);
                 const routeMap = {};
                 requests.forEach(req => {
                     if (!routeMap[req.routeId]) {
                         routeMap[req.routeId] = {
                             routeId: req.routeId,
                             routeName: allVehicleRoutes?.find(rv => String(rv.idx) === String(req.routeId))?.nm || `Route ${req.routeId}`,
                             tripsCount: 0, expectedDist: 0, actualDist: 0, actualDur: 0
                         };
                     }
                     const rEntry = routeMap[req.routeId];
                     rEntry.tripsCount++;
                     
                     if (routeStats) {
                         routeStats.forEach(rs => {
                             if (rs.routes) {
                                rs.routes.forEach(r => {
                                    if (String(r.routeId) === String(req.routeId) && r.tripStats) {
                                        if (!rEntry.expectedDist) rEntry.expectedDist = r.routeDst;
                                        r.tripStats.forEach(ts => {
                                            let match = false;
                                            if (ts.requestIdx && String(ts.requestIdx) === String(req.idx)) match = true;
                                            else if (!ts.requestIdx && String(ts.driverId) === String(driverIdForHistory) && Math.abs((ts.startTs || 0) - (req.ts || 0)) < 300000) match = true;
                                            
                                            if (match) {
                                                rEntry.actualDist += (ts.dst || 0);
                                                rEntry.actualDur += (ts.dur || 0);
                                            }
                                        });
                                    }
                                });
                             }
                         });
                     }
                 });
                 const routesArr = Object.values(routeMap);
                 routesArr.forEach(r => {
                     if (!r.expectedDist) {
                        const fallbackDist = allVehicleRoutes?.find(rv => String(rv.idx) === String(r.routeId))?.totalDistance || 0;
                        r.expectedDist = fallbackDist * 1000;
                     }
                 });
                 setDailyRoutes(routesArr);
             });
        });
    }

    // Level 3: Fetch Trips for Route & Date
    const fetchTripsForRoute = (dateStr, rId) => {
        if (!driverIdForHistory) return;
        setLoading(true);
        const dayStart = new Date(dateStr).setHours(0,0,0,0);
        const dayEnd = new Date(dateStr).setHours(23,59,59,999);

        getRouteTrips({ driverId: driverIdForHistory, routeId: rId, from: dayStart, to: dayEnd }, (err, requests) => {
             if (err || !requests || !requests.length) { setRouteTrips([]); setLoading(false); return; }
             const vids = [...new Set(requests.map(r => r.vid))];
             
             getRouteStats({ fromTs: dayStart, toTs: dayEnd, vids: vids }, (statErr, routeStats) => {
                 setLoading(false);
                 const trips = [];
                 
                 requests.forEach(req => {
                     const trip = { ...req, expectedDist: 0, actualDist: 0, deviated: 0, duration: 0, startTs: req.ts || req.startTs, endTs: 0, score: 0 };
                     
                     if (routeStats) {
                        routeStats.forEach(rs => {
                            if (rs.routes) {
                               rs.routes.forEach(r => {
                                   if (String(r.routeId) === String(req.routeId) && r.tripStats) {
                                       trip.expectedDist = r.routeDst; // Capture generic route dist
                                       r.tripStats.forEach(ts => {
                                           let match = false;
                                           if (ts.requestIdx && String(ts.requestIdx) === String(req.idx)) match = true;
                                           else if (!ts.requestIdx && String(ts.driverId) === String(driverIdForHistory) && Math.abs((ts.startTs || 0) - (req.ts || 0)) < 300000) match = true;
                                           
                                           if (match) {
                                               trip.actualDist = ts.dst || 0;
                                               trip.duration = ts.dur || 0;
                                               trip.startTs = ts.startTs || trip.startTs;
                                               trip.endTs = ts.endTs || 0;
                                               trip.endTs = ts.endTs || 0;
                                               if (rs.idx) trip.vid = rs.idx; // Ensure vid is captured from stats if available
                                               trip.deviated = ts.deviated || ts.devDst || 0;
                                           }
                                       });
                                   }
                               });
                            }
                        });
                     }
                     if (!trip.expectedDist) {
                        const fallbackDist = allVehicleRoutes?.find(rv => String(rv.idx) === String(req.routeId))?.totalDistance || 0;
                        trip.expectedDist = fallbackDist * 1000;
                     }
                     trips.push(trip);
                 });
                 // Sort by time desc
                 trips.sort((a,b) => (b.startTs || 0) - (a.startTs || 0));
                 setRouteTrips(trips);
             });
        });
    }

    const sortedDates = Object.keys(groupedHistory).sort((a,b) => new Date(b) - new Date(a));
    const showLevel1 = !selectedDateParam && !selectedRouteParam;
    const showLevel2 = selectedDateParam && !selectedRouteParam;
    const showLevel3 = selectedDateParam && selectedRouteParam;

    return (
        <div className="panel-main past-rides route-history">
            <header className="past-rides-header">
                <div className="bread-container">
                   <Breadcrumb paths={getBreadcrumbs()} />
               </div>
               <DatePicker customMaxDate={getISODate()} />
            </header>

            {loading && <div className="spinner-center"><Spinner /></div>}
            
            {/* VIEW 1: DATE LIST */}
            {showLevel1 && !loading && sortedDates.length === 0 && (
                <div className="no-rides"><span>No route history found.</span></div>
            )}
            {showLevel1 && !loading && sortedDates.map(date => {
                const data = groupedHistory[date];
                return (
                    <article key={date} className="card widget-group">
                        <header className="widget-header"><Title variant="md">{date}</Title></header>
                        <div className="widget-group keyvals">
                            <main className="widget-main"><header>Routes</header><article>{data.routeCount}</article></main>
                            <main className="widget-main"><header>Expected Distance</header><article>{(data.totalExpectedDist/1000).toFixed(0)} km</article></main>
                            <main className="widget-main"><header>Actual Distance</header><article>{(data.totalActualDist/1000).toFixed(0)} km</article></main>
                        </div>
                        <div className="widget-link">
                            <Button variant="ghost" rightIcon={<ArrowRightIcon />} onClick={() => navigate({ pathname: PATHS.ROUTE_HISTORY, search: createSearchParams({ date: date }).toString() })}>
                                View Route
                            </Button>
                        </div>
                    </article>
                );
            })}

            {/* VIEW 2: DAILY ROUTE LIST (Aggregated by Route) */}
            {showLevel2 && !loading && dailyRoutes.length === 0 && (
                 <div className="no-rides"><span>No routes found for {selectedDateParam}.</span></div>
            )}
            {showLevel2 && !loading && dailyRoutes.map((route, idx) => (
                 <article key={idx} className="card widget-group">
                    <header className="widget-header"><Title variant="md">{route.routeName}</Title></header>
                    <div className="widget-group keyvals">
                        <main className="widget-main"><header>Trips</header><article>{route.tripsCount}</article></main>
                        <main className="widget-main"><header>Expected Distance</header><article>{(route.expectedDist/1000).toFixed(0)} km</article></main>
                        <main className="widget-main"><header>Actual Total Dist.</header><article>{(route.actualDist/1000).toFixed(0)} km</article></main>
                    </div>
                    <div className="widget-link">
                        <Button variant="ghost" rightIcon={<ArrowRightIcon />} onClick={() => navigate({ pathname: PATHS.ROUTE_HISTORY, search: createSearchParams({ date: selectedDateParam, routeId: route.routeId }).toString() })}>
                            View Trips
                        </Button>
                    </div>
                </article>
            ))}

            {/* VIEW 3: TRIP LIST (Detailed Time-wise cards) */}
            {showLevel3 && !loading && routeTrips.length === 0 && (
                 <div className="no-rides"><span>No trips found.</span></div>
            )}
            {showLevel3 && !loading && routeTrips.map((trip, idx) => {
                 const vehicle = userVehicles ? userVehicles.find(v => String(v.idx) === String(trip.vid)) : null;
                 const regNo = vehicle ? vehicle.reg : trip.reg; // Fallback to trip.reg if available

                 const timeRange = `${getLocalisedTime(trip.startTs)} - ${trip.endTs ? getLocalisedTime(trip.endTs) : getLocalisedTime(trip.startTs)} ${regNo ? `(${regNo})` : ''}`;
                 return (
                    <article key={idx} className="card widget-group">
                        <header className="widget-header"><Title variant="md">{timeRange}</Title></header>
                        <div className="widget-group keyvals">
                            <main className="widget-main"><header>Expected Dist</header><article>{(trip.expectedDist/1000).toFixed(0)} km</article></main>
                            <main className="widget-main"><header>Actual Dist</header><article>{(trip.actualDist/1000).toFixed(0)} km</article></main>
                            <main className="widget-main"><header>Ride Duration</header><article>{secondsToHms(trip.duration)}</article></main>
                        </div>
                        <div className="widget-link">
                            {trip.actualDist > 0 ? (
                             <Button variant="ghost" rightIcon={<ArrowRightIcon />} onClick={() => {
                                 // Use the new accessible route definition: Route History -> Past Rides -> Ride Details
                                 navigate(`${PATHS.ROUTE_HISTORY}/${PATHS.PAST_RIDES}/${PATHS.RIDE_DETAILS}?reg=${regNo || trip.vid}&route=${trip.routeId}&ts=${trip.startTs}&vid=${trip.vid}&source=route_history&date=${selectedDateParam}&driverId=${driverIdForHistory}`, {
                                    state: { ride: { ...trip, sdt: trip.startTs/1000, edt: trip.endTs/1000, routeDst: trip.expectedDist, dst: trip.actualDist, deviated: trip.deviated } } 
                                 });
                             }}>
                                View Trip
                            </Button>
                            ) : null}
                        </div>
                    </article>
                 );
            })}
        </div>
    );
};

export default RouteHistory;
